'use client';

/* eslint-disable react-refresh/only-export-components */

import {
  DotsThreeVerticalIcon,
  ListMagnifyingGlassIcon,
  TrashIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CategoryChip } from '@/components/CategoryChip';
import { cn } from '@/lib/utils';
import type { RecurringPayment } from '@/lib/api';
import { cadenceLabel } from '@/lib/recurring/cadence';
import { canDeleteRecurringPayment, type RecurringPaymentAction } from './actions';

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const statusLabel: Record<RecurringPayment['status'], { label: string; cls: string }> = {
  Detected: { label: 'detectado', cls: 'border-warning-border bg-warning-surface text-warning-text' },
  Active: { label: 'ativo', cls: 'border-success-border bg-success-surface text-success-text' },
  Cancelling: { label: 'encerrado', cls: 'border-border bg-surface-sunken text-text-secondary' },
  Cancelled: { label: 'cancelado', cls: 'border-border bg-surface-sunken text-text-secondary' },
  FailedCancellation: { label: 'revisar', cls: 'border-error-border bg-error-surface text-error-text' },
  Completed: { label: 'concluído', cls: 'border-success-border bg-success-surface text-success-text' },
  Rejected: { label: 'rejeitado', cls: 'border-border bg-surface-sunken text-text-secondary' },
  Missed: { label: 'atrasado', cls: 'border-warning-border bg-warning-surface text-warning-text' },
  EarlySettlementSuspected: { label: 'quitado?', cls: 'border-info-border bg-info-surface text-info-text' },
};

function formatDate(date: string | null): string {
  if (!date) return 'sem previsão';
  return new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

export function getRecurringPaymentCategoryDisplay(item: RecurringPayment) {
  const label = item.categoryTranslated ?? item.category ?? item.parentCategoryTranslated ?? 'Outros';
  return {
    id: item.categoryId ?? item.category ?? item.categoryTranslated ?? item.parentCategoryTranslated ?? 'uncategorized',
    label,
    iconName: item.categoryIcon ?? item.parentCategoryIcon ?? 'circle-dashed',
  };
}

function StatusIndicator({ item }: { item: RecurringPayment }) {
  const status = statusLabel[item.status];
  if (item.status === 'Active') {
    return (
      <span
        className="mt-1 inline-flex size-2 shrink-0 rounded-full bg-success"
        title="Ativo"
        aria-label="Ativo"
      />
    );
  }

  return (
    <span className={cn('shrink-0 rounded border px-1.5 py-0.5 text-2xs font-bold uppercase', status.cls)}>
      {status.label}
    </span>
  );
}

interface Props {
  item: RecurringPayment;
  onAction: (id: string, action: RecurringPaymentAction) => void;
  onOpenHistory?: (item: RecurringPayment) => void;
  onDeleteRequest?: (item: RecurringPayment) => void;
}

export function RecurringPaymentCard({ item, onAction, onOpenHistory, onDeleteRequest }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const run = (action: RecurringPaymentAction) => {
    setMenuOpen(false);
    onAction(item.id, action);
  };

  const requestDelete = () => {
    setMenuOpen(false);
    onDeleteRequest?.(item);
  };

  const name = item.merchantName ?? item.categoryTranslated ?? item.category ?? 'Recorrente';
  const category = getRecurringPaymentCategoryDisplay(item);
  const dateLabel = item.status === 'Completed' ? 'finalizado' : formatDate(item.nextExpectedDate);
  const detail = `${category.label} - ${cadenceLabel(item.cadence)}`;
  const canDelete = canDeleteRecurringPayment(item);

  return (
    <Card variant="flat" className={cn(
      'flex flex-col gap-3 rounded-[var(--radius-3xl)] border-border bg-surface p-3 shadow-[var(--shadow-1)] transition-colors hover:bg-surface-sunken sm:p-4',
      item.status === 'FailedCancellation' && 'border-error-border bg-error-surface',
      item.status === 'Detected' && 'border-warning-border bg-warning-surface',
    )}>
      <div className="grid grid-cols-[2.25rem_minmax(0,1fr)_auto] gap-3 sm:grid-cols-[2.5rem_minmax(0,1fr)_auto]">
        <CategoryChip
          categoryKey={category.id}
          label={category.label}
          iconName={category.iconName}
          title={category.label}
          iconOnly
          size="md"
          className="mt-0.5"
        />
        <div className="min-w-0">
          <div className="flex min-w-0 items-start gap-1.5">
            <h4 className="line-clamp-2 min-w-0 flex-1 text-sm/snug font-bold text-text-primary" title={name}>
              {name}
            </h4>
            <StatusIndicator item={item} />
          </div>
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs font-medium text-text-secondary">
            <span className="max-w-full min-w-0 truncate">{category.label}</span>
            <span aria-hidden>-</span>
            <span>{cadenceLabel(item.cadence)}</span>
            <span aria-hidden>-</span>
            <span>{dateLabel}</span>
            <span className="sr-only">{detail}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-start gap-1">
          <div className="min-w-[4.9rem] text-right">
            <p className="font-mono text-sm font-bold text-text-primary tabular-nums sm:text-base">
              {brl.format(item.amount)}
              {item.cadence === 'Yearly' && (
                <span className="text-xs font-medium text-text-tertiary">/ano</span>
              )}
            </p>
          </div>
          <div ref={menuRef} className="relative shrink-0">
            <Button
              variant="ghost-icon"
              size="icon-sm"
              aria-label="Ações do recorrente"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((value) => !value)}
            >
              <DotsThreeVerticalIcon size={18} weight="bold" />
            </Button>
            {menuOpen && (
              <div role="menu" className="absolute top-full right-0 z-20 mt-1 min-w-44 overflow-hidden rounded-xl border border-border bg-surface shadow-lg">
                {item.status === 'Detected' && (
                  <>
                    <button type="button" role="menuitem" onClick={() => run('confirm')} className="block w-full px-3 py-2 text-left text-sm text-text-primary hover:bg-accent">
                      Confirmar
                    </button>
                    <button type="button" role="menuitem" onClick={() => run('reject')} className="block w-full px-3 py-2 text-left text-sm text-error-text hover:bg-error-surface">
                      Rejeitar
                    </button>
                  </>
                )}
                {(item.status === 'Active' || item.status === 'FailedCancellation') && (
                  <button type="button" role="menuitem" onClick={() => run('cancel')} className="block w-full px-3 py-2 text-left text-sm text-error-text hover:bg-error-surface">
                    Encerrar
                  </button>
                )}
                {(item.status === 'Rejected' || item.status === 'Cancelled' || item.status === 'Completed') && (
                  <button type="button" role="menuitem" onClick={() => run('reactivate')} className="block w-full px-3 py-2 text-left text-sm text-text-primary hover:bg-accent">
                    Reativar
                  </button>
                )}
                {onDeleteRequest && canDelete && (
                  <>
                    <div className="h-px bg-divider" />
                    <button type="button" role="menuitem" onClick={requestDelete} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-error-text hover:bg-error-surface">
                      <TrashIcon size={14} weight="bold" />
                      Excluir
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {onOpenHistory && (
        <div className="flex justify-end border-t border-border pt-3">
          <Button variant="ghost" size="sm" onClick={() => onOpenHistory(item)}>
            <ListMagnifyingGlassIcon size={15} weight="bold" />
            Histórico
          </Button>
        </div>
      )}
      {item.status === 'FailedCancellation' && (
        <div className="flex items-center gap-2 border-t border-error-border pt-3 text-xs font-medium text-error-text">
          <XCircleIcon size={14} weight="bold" />
          Houve cobrança depois do cancelamento. Vale revisar.
        </div>
      )}
    </Card>
  );
}
